import {set, isArray, isPlainObject, mergeWith, entries, some, isString, ceil, trim, endsWith, cloneDeep} from 'lodash';
import {BOS_HOSTS} from '../config/default';
import {Base64} from 'js-base64';
import delay from 'delay';

export function expandConfig(data) {
    if (!isPlainObject(data)) {
        return data;
    }
    return entries(data).reduce(function (ret, [key, value]) {
        if (isArray(value)) {
            value = value.map(expandConfig);
        }
        else if (isPlainObject(value)) {
            value = expandConfig(value);
        }
        set(ret, key, value);
        return ret;
    }, {});
}

export function mergeConfig(...sources) {
    return mergeWith({}, ...sources, function (objValue, srcValue) {
        if (isArray(srcValue)) {
            return srcValue;
        }
    });
}

// 后端采用GBK编码计算字节长度，前端这里按GBK对齐 https://zh.wikipedia.org/wiki/%E6%B1%89%E5%AD%97%E5%86%85%E7%A0%81%E6%89%A9%E5%B1%95%E8%A7%84%E8%8C%83
export function getByteLength(string) {
    let realLength = 0;
    string.trim().split('').forEach((item, index) => {
        let charCode = string.charCodeAt(index);
        if (charCode >= 0 && charCode < 128) {
            realLength += 1;
        }
        else {
            realLength += 2;
        }
    });
    return realLength;
}

// 一个换行符 = 一个中文 = 两个字符
// 替换规则，首尾换行不管多少行都不算，中间连续换行只算一个换行符
export function getTextAreaByteLength(val = '') {
    return getByteLength(val.trim().replace(/(\n)+/g, '\\n'));
}

export function formatString(message, data) {
    return message.replace(/\{(\w+)\}/g, (match, key) => {
        if (data[key] != null) {
            return data[key];
        }
        return match;
    });
}

export function formatTotal(total) {
    return total > 9999 ? '9999+' : total;
}

export function isURL(url) {
    return /^https?:/i.test(url);
}


export function isBosUrl(url, bosHosts = BOS_HOSTS) {
    let host = getHostOfUrl(url);
    if (!host) {
        return false;
    }
    host = host.toLowerCase();
    return some(bosHosts, h => (isString(h) ? h === host : h.test(host)));
}

export function isGifUrl(url) {
    return endsWith(url.toLowerCase(), '.gif');
}

export function isPngUrl(url) {
    return endsWith(url.toLowerCase(), '.png');
}

export function getHostOfUrl(url) {
    let hostStart = url.indexOf('://');
    if (hostStart < 0) {
        return;
    }
    hostStart += '://'.length;
    let hostEnd = url.indexOf('/', hostStart);

    if (hostEnd < 0) {
        return url.substring(hostStart);
    }
    return url.substring(hostStart, hostEnd);
}

export function getGetPastSeconds(actual = false) {
    const startTime = Date.now();
    return function () {
        let pastSeconds = (Date.now() - startTime) / 1000;
        // 向上取整，对应统计报表就是 xx秒以内
        // 四舍五入的话，变成 m.5~n.4999秒之间 (n=m+1)，看起来有点怪
        return actual ? pastSeconds : ceil(pastSeconds, 1);
    };
}


export function checkNameByteLength(name, maxLength) {
    const length = getByteLength(trim(name));
    return length > 0 && length <= maxLength;
}

export function jsonParseWithCatch(data, errorMessage) {
    if (process.env.NODE_ENV === 'development') {
        if (!errorMessage) {
            throw new Error('没有传入errorMessage参数!');
        }
    }
    let result;
    try {
        result = JSON.parse(data);
    }
    catch (error) {
        error.message = errorMessage;
        throw error;
    }
    return result;
}

export function formatBosResourceURL(origin, version, filename) {
    return `${origin}/sdk/${version}/${filename}`;
}

export function updateObjectValueByKey(obj, key, value) {
    let [first, ...rest] = isString(key) ? key.split('.') : key;
    return {
        ...obj,
        [first]: rest.length ? updateObjectValueByKey(obj[first] || {}, rest, value) : value
    };
}

/**
 * 将文本复制到剪贴板
 *
 * @param {string} value 要复制的文本值
 * @returns {boolean} 如果复制成功则返回true，否则返回false
 */
export function copyText(value) {
    let fakeElem = document.createElement('input');
    fakeElem.value = value;
    fakeElem.readonly = true;
    document.body.appendChild(fakeElem);
    fakeElem.select();
    try {
        document.execCommand('copy');
    }
    catch (err) {
        return false;
    }
    document.body.removeChild(fakeElem);
    return true;
}

/**
 * base64编码电话号码。
 *
 * @param telephone - 电话号码。
 * @returns 返回已被base64编码的字符串。
 */
export function encodeTelephone(telephone) {
    return Base64.encode(`bass ${telephone}`);
}

export function getCharLength(str = '') {
    // 1个中文按2个字节计算，英文、数字等按1个字节计算
    const charLength = str.replace(/[^\x00-\xff]/g, '**').length;
    return charLength;
}
export function getImageSize(imgSrc) {
    const img = new Image();
    img.src = imgSrc;
    return new Promise((resolve, reject) => {
        img.onload = () => {
            resolve({
                width: img.width,
                height: img.height
            });
        };
        img.onerror = () => {
            resolve({
                width: 0,
                height: 0
            });
        };
    });
}

function isOptimal(text, min, max) {
    const len = getCharLength(text);
    if (len >= min && len <= max) {
        return 1;
    }
    return 0;
}

/**
 * 将列表按照标题和描述是否是最优字符串长度进行排序
 *
 * @param list 待排序的列表
 * @param options 排序选项，包括结果的最小值和最大值，标题描述的最小值和最大值
 * @returns 排序后的新列表
 */
export function sortResultByTitleAndDesc(list, options) {
    const newList = list.map(item => {
        const records = cloneDeep(item.records).map(i => {
            const {resultMin, resultMax, titleDescMin, titleDescMax} = options;
            const priority = isOptimal(i.result, resultMin, resultMax)
                            + isOptimal(i.titleDesc, titleDescMin, titleDescMax);
            return {...i, priority};
        }).sort((a, b) => {
            return b.priority - a.priority;
        }).map(i => {
            delete i.priority;
            return i;
        });
        return {
            ...item,
            records
        };
    });
    return newList;
}
/**
 * 获取文本在指定字体和字号下的实际宽度
 *
 * @param text 要测量宽度的文本
 * @param options 可选参数对象，包含以下属性：
 *   - size: 字号，默认为14
 *   - family: 字体，默认为'sans-serif'
 * @returns 返回文本的实际宽度
 */
export function getActualWidthOfChars(text, options = {}) {
    const {size = 14, family = 'sans-serif'} = options;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `${size}px ${family}`;
    return ctx.measureText(text).width;
}
const base64ExtensionStr = ';base64,';
export function removeDataURIPrefix(content) {
    return content.substring(content.indexOf(base64ExtensionStr) + base64ExtensionStr.length);
}

// 视频转base64
export function getFileBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function (e) {
            resolve(e.target.result);
        };
        reader.onerror = function (e) {
            // https://developer.mozilla.org/zh-CN/docs/Web/API/FileReader/onerror 使用reader.error
            // 原error为 DOMException，缺少stack且message不可更改，会导致formatError修改错误信息时出错，这里new一个可更改的error抛出
            const error = new Error(reader.error);
            reject(error);
        };
        reader.readAsDataURL(file);
    });
}
  // 根据url获取视频的宽高信息
export function getVideoDimension(url, timeout = 10 * 1000) {
    let promise = new Promise(function (resolve, reject) {
        let video = document.createElement('video');
        video.addEventListener('loadedmetadata', function () {
            resolve({width: video.videoWidth, height: video.videoHeight});
        });
        video.onerror = reject;
        video.src = url;
    });
    return Promise.race([
        promise,
        delay.reject(timeout, {
            value: new Error('Load video timeout')
        })
    ]);
}